import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { StatusBadge } from "../../components/StatusBadge";
import { allSlugs } from "../client/programmeStructure";

const ALL_SLUGS = allSlugs();
const pct = (c) => ALL_SLUGS.length ? Math.round(((c.completed_slugs || []).filter((s) => ALL_SLUGS.includes(s)).length / ALL_SLUGS.length) * 100) : 0;

function ProgressBar({ value }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-bordeaux-tint">
      <div className="h-full rounded-full bg-bordeaux transition-all" style={{ width: `${value}%` }} />
    </div>
  );
}

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [archived, setArchived] = useState(false);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", activity: "", start_date: "", end_date: "" });
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const load = () => api.get(`/admin/clients?archived=${archived}`).then(({ data }) => setClients(data)).catch(() => {});
  useEffect(() => { load(); }, [archived]);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/admin/clients", form);
      setModal(false);
      toast.success(`Invitation envoyée à ${form.email} 🤍`);
      setForm({ first_name: "", last_name: "", email: "", activity: "", start_date: "", end_date: "" });
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-sans text-3xl font-medium text-ink sm:text-4xl">Mes clientes</h1>
          <p className="mt-2 font-light text-ink-soft">Suivi & avancement</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-full border border-line bg-white p-1 text-sm">
            <button
              data-testid="filter-active"
              onClick={() => setArchived(false)}
              className={`rounded-full px-4 py-1.5 transition ${!archived ? "bg-bordeaux text-white" : "text-ink-soft"}`}
            >
              Actives
            </button>
            <button
              data-testid="filter-archived"
              onClick={() => setArchived(true)}
              className={`rounded-full px-4 py-1.5 transition ${archived ? "bg-bordeaux text-white" : "text-ink-soft"}`}
            >
              Terminées
            </button>
          </div>
          <button
            data-testid="add-client-button"
            onClick={() => setModal(true)}
            className="flex items-center gap-2 rounded-full bg-bordeaux px-5 py-2.5 text-sm font-medium text-white transition hover:bg-bordeaux-dark"
          >
            <Plus size={16} /> Nouvelle cliente
          </button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {clients.map((c, i) => (
          <motion.button
            key={c.id}
            data-testid={`client-card-${c.id}`}
            onClick={() => navigate(`/admin/clientes/${c.id}`)}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="group rounded-2xl border border-line bg-white p-6 text-left shadow-card transition hover:border-bordeaux-border hover:shadow-editorial"
          >
            <div className="flex items-start gap-4">
              <img src={c.photo} alt={c.first_name} className="h-14 w-14 rounded-full object-cover ring-2 ring-bordeaux-border" />
              <div className="flex-1">
                <p className="font-sans text-xl font-medium text-ink">{c.first_name} {c.last_name}</p>
                <p className="text-xs text-ink-muted">{c.activity}</p>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between text-sm">
              <span className="text-ink-soft">Semaine {c.current_week} · Phase {c.phase}</span>
              <span data-testid={`client-progress-${c.id}`} className="font-medium text-bordeaux">{pct(c)}%</span>
            </div>
            <div className="mt-2"><ProgressBar value={pct(c)} /></div>
            <div className="mt-4">
              {c.status && <StatusBadge status={c.status} />}
            </div>
            <div className="mt-4 border-t border-line pt-3 text-xs text-ink-muted">
              Prochain appel · {c.next_call || "à planifier"}
            </div>
          </motion.button>
        ))}
        {clients.length === 0 && (
          <p className="text-ink-muted">Aucune cliente {archived ? "archivée" : "active"} pour le moment.</p>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-editorial">
            <div className="flex items-center justify-between">
              <h2 className="font-sans font-medium text-2xl text-ink">Nouvelle cliente</h2>
              <button data-testid="close-modal" onClick={() => setModal(false)} className="text-ink-muted hover:text-bordeaux"><X size={20} /></button>
            </div>
            <form onSubmit={create} className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="client-firstname" required placeholder="Prénom" value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="rounded-xl border border-line px-4 py-2.5 outline-none focus:border-bordeaux" />
                <input data-testid="client-lastname" placeholder="Nom" value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className="rounded-xl border border-line px-4 py-2.5 outline-none focus:border-bordeaux" />
              </div>
              <input data-testid="client-email" required type="email" placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-xl border border-line px-4 py-2.5 outline-none focus:border-bordeaux" />
              <input data-testid="client-activity" placeholder="Activité (ex: Coach business)" value={form.activity}
                onChange={(e) => setForm({ ...form, activity: e.target.value })}
                className="w-full rounded-xl border border-line px-4 py-2.5 outline-none focus:border-bordeaux" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-ink-soft">Début d'accompagnement</label>
                  <input data-testid="client-start-date" type="date" value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="mt-1 w-full rounded-xl border border-line px-4 py-2.5 text-ink-soft outline-none focus:border-bordeaux" />
                </div>
                <div>
                  <label className="text-sm text-ink-soft">Fin d'accompagnement</label>
                  <input data-testid="client-end-date" type="date" value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="mt-1 w-full rounded-xl border border-line px-4 py-2.5 text-ink-soft outline-none focus:border-bordeaux" />
                </div>
              </div>
              <div className="rounded-xl border border-bordeaux-border bg-bordeaux-tint px-4 py-3 text-sm font-light text-bordeaux">
                Un email d'invitation sera envoyé automatiquement à ta cliente pour qu'elle crée elle-même son mot de passe et active son espace.
              </div>
              {error && <p className="text-sm text-bordeaux">{error}</p>}
              <button data-testid="submit-client" type="submit"
                className="w-full rounded-full bg-bordeaux py-3 font-medium text-white transition hover:bg-bordeaux-dark">
                Créer l'espace de ma cliente
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
